import React from 'react';
import CodeBlock from '../../components/common/CodeBlock';
import AuthorInfo from '../../components/common/AuthorInfo';
import { SectionHeading, SubHeading, Para, Mark, Code, Callout, Output, Pill, MemoryBox, TwoUp } from './articleKit';

type Author = { name: string; fullName: string; avatar?: string };

const PRIMITIVES = [
 { type: 'String', desc: 'Text', example: '"Sachin"' },
 { type: 'Number', desc: 'Integers & decimals', example: '25, 99.99, -45' },
 { type: 'Boolean', desc: 'true / false', example: 'true' },
 { type: 'Undefined', desc: 'Declared, not assigned', example: 'let user;' },
 { type: 'Null', desc: 'Intentionally empty', example: 'null' },
 { type: 'BigInt', desc: 'Very large integers', example: '12345…n' },
 { type: 'Symbol', desc: 'Unique identifiers', example: 'Symbol("id")' },
];

export const JSVariables: React.FC<{ author: Author }> = ({ author }) => (
 <div className="max-w-none">
  {/* Hero header */}
  <div className="mb-10 pb-6 border-b border-gray-200 dark:border-gray-700">
   <h1 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white mb-4">Variables &amp; Data Types</h1>
   <p className="text-lg leading-8 text-gray-600 dark:text-gray-400 mb-5">
    How JavaScript stores, names, and remembers data — and the kinds of values it can hold. This is the foundation every other concept builds on.
   </p>
   <AuthorInfo name={author.name} fullName={author.fullName} avatar={author.avatar} lastUpdated="June 2026" readTime="12 min read" />
  </div>

  {/* 1. The Real Problem */}
  <section className="mb-12">
   <SectionHeading n={1}>The Real Problem</SectionHeading>
   <Para>Imagine you&apos;re building a food delivery app. You need to store a customer name, restaurant name, order amount, delivery status, delivery-partner info… and keep all of it around long enough to actually use it.</Para>
   <Para>Now suppose the system just had loose values floating around with no names attached:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `"Sachin"\n1250\ntrue` }]} defaultLanguage="javascript" id="vars-loose-values" />
   <Para>What do these mean? Who is <Code>"Sachin"</Code>? Is <Code>1250</Code> the order amount or the delivery fee? Is <Code>true</Code> the payment status or the delivery status? The system has <Mark>no way to identify or organize</Mark> the information.</Para>
   <Para>We need a mechanism to store data, name it, retrieve it later, and modify it when needed. That mechanism is the <Mark>variable</Mark>.</Para>
  </section>

  {/* 2. Definition */}
  <section className="mb-12">
   <SectionHeading n={2}>Definition</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <p className="text-[15px] leading-7 text-gray-800 dark:text-gray-200">A <Mark>variable</Mark> is a named container used to store data in memory — think of it as a label attached to a value.</p>
   </div>
   <CodeBlock snippets={[{ language: 'javascript', code: `let customerName = "Sachin";\n//   ^^^^^^^^^^^^   ^^^^^^^^\n//   variable name   value` }]} defaultLanguage="javascript" id="vars-definition" />
   <Para>The name lets us access and manipulate that value later, anywhere in the program.</Para>
  </section>

  {/* 3. Why Variables Exist */}
  <section className="mb-12">
   <SectionHeading n={3}>Why Variables Exist</SectionHeading>
   <Para>Applications constantly juggle data — user name, order amount, product price, login status, cart items. Without variables, every value would have to be hardcoded and repeated:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `console.log("Sachin");\nconsole.log("Sachin");\nconsole.log("Sachin");` }]} defaultLanguage="javascript" id="vars-hardcoded" />
   <Para>With a variable, the value lives in one place and the code stays the same even when the value changes:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `let customerName = "Sachin";\nconsole.log(customerName);  // change the value once, everywhere updates` }]} defaultLanguage="javascript" id="vars-dynamic" />
  </section>

  {/* 4. Understanding Memory */}
  <section className="mb-12">
   <SectionHeading n={4}>Understanding Memory</SectionHeading>
   <Para>When JavaScript runs <Code>let age = 25;</Code>, it allocates a slot in memory and points the name <Code>age</Code> at it. Reassigning just changes the stored value — the name keeps pointing to the same slot.</Para>
   <div className="my-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30 p-6">
    <div className="flex items-center justify-center gap-6 sm:gap-10 flex-wrap">
     <MemoryBox name="age" value="25" />
     <div className="flex flex-col items-center text-gray-500 dark:text-gray-500">
      <span className="text-[11px] font-mono mb-1">age = 30</span>
      <span className="text-2xl leading-none">→</span>
     </div>
     <MemoryBox name="age" value="30" tone="amber" />
    </div>
   </div>
   <Para>Variables let us work with memory through names, without ever worrying about raw memory addresses.</Para>
  </section>

  {/* 5. Declaring Variables */}
  <section className="mb-12">
   <SectionHeading n={5}>Declaring Variables: var, let, const</SectionHeading>
   <Para>JavaScript has three declaration keywords, and they behave differently:</Para>

   <SubHeading>var — the old way</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `var city = "Delhi";` }]} defaultLanguage="javascript" id="vars-var" />
   <Para>It&apos;s function-scoped and can be silently redeclared, which leads to confusing bugs. Modern JavaScript rarely uses it.</Para>

   <SubHeading>let — the modern reassignable variable</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `let score = 10;\nscore = 20;   // ✅ valid — let can be reassigned` }]} defaultLanguage="javascript" id="vars-let" />
   <Para>Block-scoped, reassignable, and safer than <Code>var</Code>.</Para>

   <SubHeading>const — for values that never get reassigned</SubHeading>
   <CodeBlock snippets={[{ language: 'javascript', code: `const country = "India";\ncountry = "USA";   // ❌ TypeError: Assignment to constant variable` }]} defaultLanguage="javascript" id="vars-const" />
  </section>

  {/* 6. let vs const */}
  <section className="mb-12">
   <SectionHeading n={6}>let vs const</SectionHeading>
   <Callout tone="tip" title="Rule of thumb">Use <Code>const</Code> by default. Reach for <Code>let</Code> only when the value genuinely needs to change.</Callout>
   <CodeBlock snippets={[{ language: 'javascript', code: `const taxRate = 18;     // never changes -> const\nlet orderAmount = 1000; // will change -> let\norderAmount += 500;` }]} defaultLanguage="javascript" id="vars-let-vs-const" />
   <Para>Defaulting to <Code>const</Code> makes code safer and easier to reason about — a reader instantly knows the value won&apos;t move.</Para>
  </section>

  {/* 7. What are data types */}
  <section className="mb-12">
   <SectionHeading n={7}>What Are Data Types?</SectionHeading>
   <Para>Variables store values; <Mark>data types</Mark> define what kind of value is being stored. JavaScript sorts every value into one of two families: primitives and reference types.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `let name = "Sachin";    // String\nlet age = 25;           // Number\nlet isPremium = true;   // Boolean` }]} defaultLanguage="javascript" id="vars-types-intro" />
  </section>

  {/* 8. Primitive data types */}
  <section className="mb-12">
   <SectionHeading n={8}>Primitive Data Types</SectionHeading>
   <Para>There are <Mark>seven</Mark> primitives. Each holds a single, immutable value:</Para>
   <div className="my-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
    {PRIMITIVES.map((p) => (
     <div key={p.type} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      <div className="text-sm font-bold text-amber-600 dark:text-amber-400">{p.type}</div>
      <div className="text-[12px] text-gray-600 dark:text-gray-400 mt-0.5">{p.desc}</div>
      <div className="mt-2 text-[11px] font-mono text-gray-600 dark:text-gray-300 break-words">{p.example}</div>
     </div>
    ))}
   </div>
   <Callout tone="info" title="One number to rule them all">Unlike many languages, JavaScript uses a single <Code>Number</Code> type for both integers and decimals. <Code>BigInt</Code> exists separately only for integers too large for <Code>Number</Code>.</Callout>
   <Para><Code>undefined</Code> vs <Code>null</Code> trips up most beginners — they look similar but mean different things:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `let user;                 // undefined — declared but never assigned\nlet selectedUser = null;  // null — intentionally empty` }]} defaultLanguage="javascript" id="vars-null-undefined" />
  </section>

  {/* 9. Reference data types */}
  <section className="mb-12">
   <SectionHeading n={9}>Reference Data Types</SectionHeading>
   <Para>Objects (and arrays, which are objects too) are <Mark>reference types</Mark>. They can hold many values and are stored and copied differently from primitives.</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `const user = {\n  name: "Sachin",\n  age: 25,\n};\n\nconst skills = ["JavaScript", "NodeJS", "React"];` }]} defaultLanguage="javascript" id="vars-reference" />
   <Callout tone="info">We cover how references are stored and copied in depth in the <Mark>Objects</Mark> section.</Callout>
  </section>

  {/* 10. Dynamic typing */}
  <section className="mb-12">
   <SectionHeading n={10}>Dynamic Typing</SectionHeading>
   <Para>JavaScript is <Mark>dynamically typed</Mark> — a variable can hold a value of any type, and that type can change at runtime:</Para>
   <CodeBlock snippets={[{ language: 'javascript', code: `let value = 100;     // Number\nvalue = "Hello";     // now a String\nvalue = true;        // now a Boolean — all valid` }]} defaultLanguage="javascript" id="vars-dynamic-typing" />
   <Para>This flexibility speeds up development but also lets type-related bugs slip in — which is exactly the gap <Mark>TypeScript</Mark> was created to close.</Para>
  </section>

  {/* 11. Common mistakes */}
  <section className="mb-12">
   <SectionHeading n={11}>Common Mistakes</SectionHeading>
   <Callout tone="warning" title="Using var everywhere">Prefer <Code>const</Code> (or <Code>let</Code>). <Code>var</Code>&apos;s function scope and redeclaration cause subtle bugs.</Callout>
   <Callout tone="warning" title="Confusing null and undefined"><Code>undefined</Code> = not assigned yet; <Code>null</Code> = intentionally empty. Use them deliberately.</Callout>
   <Callout tone="warning" title="Using let when const fits">If a value never changes, <Code>const</Code> documents that intent and prevents accidental reassignment.</Callout>
  </section>

  {/* 12. Interview concepts */}
  <section className="mb-12">
   <SectionHeading n={12}>Interview Concepts</SectionHeading>
   <Para>Interviewers return to the same handful of questions here:</Para>
   <div className="flex flex-wrap gap-2">
    {['var vs let vs const', 'null vs undefined', 'Static vs dynamic typing', 'Primitive vs reference types', 'Why TypeScript exists'].map((q) => <Pill key={q}>{q}</Pill>)}
   </div>
  </section>

  {/* 13. Mental model */}
  <section className="mb-12">
   <SectionHeading n={13}>Mental Model</SectionHeading>
   <Para>Think of a variable as a <Mark>labeled storage box</Mark>. The label (the name) lets you find the value whenever you need it; the data type describes what kind of thing the box is allowed to hold.</Para>
   <div className="my-6 flex justify-center">
    <MemoryBox name="customerName" value='"Sachin"' tone="amber" />
   </div>
  </section>

  {/* 14 & 15 */}
  <section className="mb-4">
   <SectionHeading n={14}>Interview One-Liner &amp; Takeaway</SectionHeading>
   <div className="border-l-2 border-amber-500 bg-gray-50 dark:bg-gray-800/40 rounded-r-lg px-5 py-4 mb-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400 mb-2">Interview One-Liner</div>
    <p className="text-[15px] leading-7 font-medium text-gray-800 dark:text-gray-200">
     Variables are named memory locations used to store data, while data types define the kind of values that can be stored and manipulated.
    </p>
   </div>
   <Callout tone="info" title="Most important takeaway">
    Variables store and manage data; data types tell JavaScript how that data should behave. Master both and you have the foundation for every other JavaScript concept.
   </Callout>
  </section>
 </div>
);

export default JSVariables;
